import React, { useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    VStack,
    Image,
    Text,
    Box,
    Button,
    Skeleton,
    useColorModeValue,
    useDisclosure
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

const StampReward = ({ isOpen, onClose, isLoading, isError, reward, refreshData }) => {
    const textColor = useColorModeValue("custom.800", "gray.300");
    const { t } = useTranslation();
    
    // For QR code preview modal
    const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
    const [previewImage, setPreviewImage] = useState('');

    const handleImageClick = (qrCodeUrl) => {
        setPreviewImage(qrCodeUrl);
        onPreviewOpen();
    };

    const handleClose = async () => {
        await refreshData();
        onClose();
    };

    if (!reward && !isLoading) return null;

    if (isError) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{t("error_title")}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text color="red.500">{t("error_message")}</Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="red" onClick={onClose} w="full">
                            {t("close_button")}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    }

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {isLoading ? <Skeleton height="20px" width="70%" /> : reward?.data?.title}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="center">
                            {isLoading ? (
                                <Skeleton height="200px" width="100%" />
                            ) : reward?.data?.image_url ? (
                                <Box width="100%" overflow="hidden" borderRadius="md">
                                    <Image
                                        src={reward.data.image_url}
                                        alt={reward.data.title}
                                        width="100%"
                                        height="180px"
                                        objectFit="cover"
                                    />
                                </Box>
                            ) : null}

                            {isLoading ? (
                                <Skeleton height="40px" width="80%" />
                            ) : (
                                <Text textAlign="center" fontSize="md" color={textColor}>
                                    {reward?.data?.description}
                                </Text>
                            )}

                            <Box p={3} borderRadius="md">
                                {isLoading ? (
                                    <Skeleton height="180px" width="180px" />
                                ) : (
                                    <Image
                                        src={reward?.qrCode}
                                        alt="Stamp Reward QR Code"
                                        boxSize="170px"
                                        cursor="pointer"
                                        onClick={() => handleImageClick(reward?.qrCode)}
                                        _hover={{ opacity: 0.8 }}
                                    />
                                )}
                            </Box>
                            <Text color={textColor} position="relative" top="-1.5rem" fontSize="sm" fontWeight="semi">
                                {t('reward_modal.qr_instruction')}
                            </Text>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="teal" onClick={handleClose} w="full" isLoading={isLoading}>
                            {t('reward_modal.close_button')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* QR Code Preview Modal */}
            <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="xs">
                <ModalOverlay />
                <ModalContent borderRadius="xl">
                    <ModalBody display="flex" flexDirection="column" alignItems="center">
                        <Box
                            position="relative"
                            width="300px"
                            height="300px"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Image
                                src={previewImage}
                                alt="QR Code Preview"
                                width="300px"
                                height="300px"
                                borderRadius="2xl"
                            />
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default StampReward;